import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { cookieDomain } from './env';

// Non-HttpOnly cookie ({"n":"<display name>"}, or {}) shared with the classified site,
// which writes the very same cookie from its /api/keycloak/session endpoint. It lets
// @bayut-sa/header paint the signed-in avatar -- with the name when we have one -- on the
// first frame, instead of showing the sign-in CTA while the Keycloak restore and the
// profile fetch are still in flight.
//
// Carries only the display name, which the header already renders anyway. Never a token:
// access/id/refresh stay where they are, and no other claim (email, phone, sub, roles) is
// copied out of the ID token.
export const USER_HINT_COOKIE = 'user_hint';

// Matches the 30-day life the classified site's session endpoint gives this cookie. Not
// derived from the token: the refresh token is an offline token with no exp, and the ID
// token's exp is the ~15-minute access lifetime, which would expire the hint almost
// immediately and bring the flash straight back.
const HINT_LIFETIME_DAYS = 30;

export const readUserHint = () => {
  const raw = Cookies.get(USER_HINT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return { name: typeof parsed?.n === 'string' ? parsed.n : null };
  } catch (err) {
    // Unparsable value: a session existed, the name is simply not known yet.
    return { name: null };
  }
};

// Called on every confirmed restore/refresh, not only at login. Sessions that predate the
// cookie never got one, so they would keep taking the slow path forever; re-stamping here
// lets them self-heal on their next page load.
export const writeUserHint = (idToken) => {
  try {
    const name = idToken ? jwtDecode(idToken)?.name : null;
    Cookies.set(
      USER_HINT_COOKIE,
      // Written even with no name so the next load still paints the avatar rather than
      // the sign-in CTA; the name fills in once the profile loads.
      JSON.stringify(typeof name === 'string' && name.trim() ? { n: name } : {}),
      {
        expires: HINT_LIFETIME_DAYS,
        path: '/',
        sameSite: 'Lax',
        // A secure cookie is dropped over plain http, which would silently disable the
        // hint in local development.
        secure: window?.location?.protocol === 'https:',
        ...(cookieDomain && { domain: cookieDomain }),
      },
    );
  } catch (err) {
    // Best effort: a missing hint only costs the next load the slower restore path.
  }
};

export const removeUserHint = () => {
  Cookies.remove(USER_HINT_COOKIE, { path: '/' });
  if (cookieDomain) Cookies.remove(USER_HINT_COOKIE, { path: '/', domain: cookieDomain });
};
